import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  Home, Inbox, MailOpen, Megaphone, Mail, Users, Briefcase, Building2, DollarSign,
  ShieldCheck, Database, BarChart3, Instagram, Cog, Archive, UserCog, TrendingUp,
  ScrollText, ArrowLeft, LogOut, Menu, X,
} from "lucide-react";

const GROUPS = [
  { label: "OVERVIEW", items: [{ to: "/hq", label: "Admin Home", icon: Home, end: true }] },
  {
    label: "OPERATIONS",
    items: [
      { to: "/hq/queue", label: "Work Queue", icon: Inbox, badge: true },
      { to: "/hq/inbox", label: "Reply Inbox", icon: MailOpen },
      { to: "/hq/coach-pilot", label: "Coach Pilot", icon: MailOpen },
      { to: "/hq/program-pilot", label: "Program Pilot", icon: MailOpen },
      { to: "/hq/campaigns", label: "Campaigns", icon: Megaphone },
      { to: "/hq/delivery", label: "Delivery", icon: Mail },
    ],
  },
  {
    label: "RECORDS",
    items: [
      { to: "/hq/coaches", label: "Coaches", icon: Users },
      { to: "/hq/jobs", label: "Jobs", icon: Briefcase },
      { to: "/hq/programs", label: "Programs", icon: Building2 },
      { to: "/hq/compensation", label: "Compensation", icon: DollarSign },
    ],
  },
  {
    label: "DATA",
    items: [
      { to: "/hq/data-quality", label: "Data Quality", icon: ShieldCheck },
      { to: "/hq/sources", label: "Sources & Imports", icon: Database },
    ],
  },
  {
    label: "GROWTH",
    items: [
      { to: "/hq/analytics", label: "Funnel & Analytics", icon: BarChart3 },
      { to: "/hq/funnel", label: "Conversion Funnel", icon: TrendingUp },
      { to: "/hq/revenue", label: "Revenue", icon: DollarSign },
      { to: "/hq/social", label: "Social Content", icon: Instagram },
    ],
  },
  {
    label: "SYSTEM",
    items: [
      { to: "/hq/system", label: "Automations & Agents", icon: Cog },
      { to: "/hq/system#backups", label: "Backups", icon: Archive },
      { to: "/hq/system#team", label: "Team Access", icon: UserCog },
      { to: "/hq/system#audit", label: "Audit Log", icon: ScrollText },
    ],
  },
];

export default function HQLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname, hash } = useLocation();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);

  useEffect(() => {
    api.get("/admin/email-queue/count").then(({ data }) => setPending(data.pending || 0)).catch(() => {});
  }, [pathname]);
  useEffect(() => { setOpen(false); }, [pathname, hash]);

  const sidebar = (
    <nav className="h-full flex flex-col bg-[#0A0A0C] border-r border-neutral-800">
      <div className="px-4 pt-5 pb-4 border-b border-neutral-800">
        <Link to="/hq" className="heading text-lg text-white" data-testid="hq-logo">
          COACHCONNECT <span className="text-blaze">ADMIN HQ</span>
        </Link>
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        {GROUPS.map((g) => (
          <div key={g.label} className="px-3 mb-4">
            <div className="text-[9px] tracking-[0.25em] text-neutral-600 px-2 mb-1">{g.label}</div>
            {g.items.map((it) => {
              const [base, frag] = it.to.split("#");
              const active = frag
                ? pathname === base && hash === `#${frag}`
                : it.end ? pathname === it.to : pathname.startsWith(it.to);
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`hq-nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`flex items-center gap-2.5 px-2 py-2 text-[13px] transition-colors border-l-2 ${
                    active ? "border-blaze text-white bg-neutral-900/60" : "border-transparent text-neutral-400 hover:text-white hover:bg-neutral-900/40"
                  }`}
                >
                  <it.icon size={15} className={active ? "text-blaze" : "text-neutral-500"} />
                  <span className="flex-1">{it.label}</span>
                  {it.badge && pending > 0 && (
                    <span data-testid="hq-queue-badge" className="bg-blaze text-white text-[10px] leading-none px-1.5 py-1 heading">{pending}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>
      <div className="border-t border-neutral-800 p-3 space-y-2">
        <Link to="/ad" data-testid="hq-return-platform" className="flex items-center gap-2 text-xs text-neutral-400 hover:text-volt px-2 py-1.5">
          <ArrowLeft size={13} /> Return to Platform
        </Link>
        <div className="flex items-center justify-between px-2">
          <span className="text-[11px] text-neutral-500 truncate" data-testid="hq-admin-email">{user?.email}</span>
          <button data-testid="hq-logout" onClick={() => { logout(); nav("/"); }} title="Log out"
            className="text-neutral-500 hover:text-blaze p-1"><LogOut size={14} /></button>
        </div>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-appbg text-white">
      {/* Mobile/tablet top bar */}
      <div className="lg:hidden sticky top-0 z-40 bg-[#0A0A0C] border-b border-neutral-800"
        style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="flex items-center gap-3 px-4 py-3">
          <button data-testid="hq-menu-toggle" onClick={() => setOpen(!open)}
            className="text-neutral-300 p-2 -ml-2 min-h-[44px] min-w-[44px] flex items-center justify-center">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <span className="heading text-sm">ADMIN HQ</span>
          {pending > 0 && (
            <Link to="/hq/queue" className="ml-auto bg-blaze text-white text-[10px] px-3 py-2.5 heading min-h-[40px] flex items-center">
              {pending} TO REVIEW
            </Link>
          )}
        </div>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-64 shrink-0" style={{ paddingTop: "env(safe-area-inset-top)" }}>{sidebar}</div>
          <div className="flex-1 bg-black/70" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="hidden lg:block fixed inset-y-0 left-0 w-60 z-30">{sidebar}</div>
      <main className="lg:pl-60">
        <Outlet />
      </main>
    </div>
  );
}
